import { configureStore, getDefaultMiddleware } from '@reduxjs/toolkit'
import { FLUSH, PAUSE, PERSIST, persistCombineReducers, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist'
import storage from 'redux-persist/lib/storage' // defaults to localStorage for web
import createSagaMiddleware from 'redux-saga'

import i18n from '@/i18n'
import { userLogout } from '@/redux/auth/actions'
import { messageHandlerSet } from '@/redux/messageHandler/actions'
import { httpClient } from '@/services/httpClient'
import {
  getHttpErrorPresentation,
  getSafeValidationDescription,
  HttpErrorKind
} from '@/services/httpError'
import reducers from './reducers'
import rootSaga from './rootsaga'

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth']
}

// Reducers
const allReducers = {
  ...reducers
}

const persistedRootReducer = persistCombineReducers(persistConfig, allReducers)

// Middleware
const sagaMiddleware = createSagaMiddleware()
const middleware = [
  ...getDefaultMiddleware({
    // To avoid reduxjs/toolkit with persist intergration
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, 'EDIT_SINGLE_ARTWORK_REQUEST']
    }
  }),
  sagaMiddleware
]

// Store
export type RootState = ReturnType<typeof persistedRootReducer>;

const store = configureStore({
  reducer: persistedRootReducer,
  middleware,
  devTools:
    process.env.NODE_ENV === 'development' &&
    (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
    (window as any).__REDUX_DEVTOOLS_EXTENSION__()
})

const persistor = persistStore(store)

sagaMiddleware.run(rootSaga)

const httpErrorDescriptionKeys: Record<HttpErrorKind, string> = {
  badRequest: 'gui.notification.http_bad_request',
  unauthorized: 'gui.notification.http_unauthorized',
  forbidden: 'gui.notification.http_forbidden',
  notFound: 'gui.notification.http_not_found',
  conflict: 'gui.notification.http_conflict',
  server: 'gui.notification.http_server_error',
  network: 'gui.notification.http_network_error',
  unexpected: 'gui.notification.http_unexpected_error'
}

httpClient.interceptors.response.use(
  res => res,
  async err => {
    const errorPresentation = getHttpErrorPresentation(err)

    if (err?.response?.status === 401) {
      store.dispatch(userLogout())
    }

    if (!err?.config?.suppressErrorNotification && errorPresentation) {
      const validationDescription = getSafeValidationDescription(
        errorPresentation.validationErrors,
        key => i18n.t(key),
        key => i18n.exists(key)
      )

      store.dispatch(messageHandlerSet({
        id: `http-request-error-${errorPresentation.kind}`,
        message: i18n.t('gui.notification.http_error_title'),
        description: validationDescription || i18n.t(
          httpErrorDescriptionKeys[errorPresentation.kind]
        ),
        duration: 6000,
        type: 'error'
      }))
    }

    return Promise.reject(err)
  }
)

export { persistor, store }
